// Calendar page (react-big-calendar + MUI)
// Elegimos react-big-calendar por integración simple con React 18 y menor fricción de estilos con MUI.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar'
import { format, parse, startOfWeek, getDay } from 'date-fns'
import enUS from 'date-fns/locale/en-US'
import useAuth from '../../hooks/useAuth'
import { normalizeCalendarRange } from './calendarRange.mjs'
import {
  listCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
} from '../../services/calendarService'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
})

const emptyForm = {
  id: null,
  title: '',
  description: '',
  location: '',
  eventType: 'appointment',
  status: 'active',
  startAt: '',
  endAt: '',
  productId: '',
  consumerId: '',
}

const readStoredProductId = () => {
  if (typeof window === 'undefined') return ''
  try {
    const raw = sessionStorage.getItem('biDashboardFilters')
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return parsed?.productId ? String(parsed.productId) : ''
  } catch (error) {
    return ''
  }
}

const formatInputDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

const CalendarPage = () => {
  const { user } = useAuth()
  const isGlobalAdmin = Boolean(user?.isGlobalAdmin) ||
    (Array.isArray(user?.roleTypes) && user.roleTypes.includes('globalAdmin'))
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [range, setRange] = useState({
    start: new Date(),
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  })
  const [selectedProductId, setSelectedProductId] = useState(readStoredProductId)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const mappedEvents = useMemo(
    () =>
      events.map((event) => ({
        ...event,
        title: event.title,
        start: new Date(event.start_at || event.startAt || event.start),
        end: new Date(event.end_at || event.endAt || event.end),
      })),
    [events]
  )

  const fetchEvents = useCallback(
    async (rangeToFetch, productId) => {
      setLoading(true)
      setError('')
      try {
        const data = await listCalendarEvents({
          start: rangeToFetch.start.toISOString(),
          end: rangeToFetch.end.toISOString(),
          productId,
        })
        setEvents(Array.isArray(data) ? data : [])
      } catch (err) {
        setError('No fue posible cargar los eventos.')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (isGlobalAdmin) {
      if (selectedProductId) {
        fetchEvents(range, selectedProductId)
      } else {
        setEvents([])
      }
      return
    }

    fetchEvents(range)
  }, [fetchEvents, range, selectedProductId, isGlobalAdmin])

  const handleRangeChange = (newRange, view) => {
    setRange(normalizeCalendarRange(newRange, view))
  }

  const handleSelectSlot = ({ start, end }) => {
    setForm({
      ...emptyForm,
      startAt: formatInputDate(start),
      endAt: formatInputDate(end),
      productId: isGlobalAdmin ? selectedProductId : '',
    })
    setDialogOpen(true)
  }

  const handleSelectEvent = (event) => {
    setForm({
      id: event.id,
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      eventType: event.event_type || 'appointment',
      status: event.status || 'active',
      startAt: formatInputDate(event.start),
      endAt: formatInputDate(event.end),
      productId: isGlobalAdmin ? event.product_id || selectedProductId : '',
      consumerId: event.consumer_id || '',
    })
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setForm(emptyForm)
  }

  const handleSubmit = async () => {
    setError('')
    if ((isGlobalAdmin && !form.productId) || !form.startAt || !form.endAt) {
      setError('start y end son obligatorios.')
      return
    }
    const payload = {
      title: form.title,
      description: form.description || null,
      location: form.location || null,
      eventType: form.eventType,
      status: form.status,
      startAt: new Date(form.startAt).toISOString(),
      endAt: new Date(form.endAt).toISOString(),
      consumerId: form.consumerId ? Number(form.consumerId) : undefined,
    }
    if (isGlobalAdmin) {
      payload.productId = Number(form.productId)
    }

    try {
      if (form.id) {
        await updateCalendarEvent(form.id, payload)
      } else {
        await createCalendarEvent(payload)
      }
      await fetchEvents(range, isGlobalAdmin ? form.productId : undefined)
      handleCloseDialog()
    } catch (err) {
      setError('No fue posible guardar el evento.')
    }
  }

  const handleDelete = async () => {
    if (!form.id) return
    try {
      await deleteCalendarEvent(form.id)
      await fetchEvents(
        range,
        isGlobalAdmin ? form.productId || selectedProductId : undefined
      )
      handleCloseDialog()
    } catch (err) {
      setError('No fue posible eliminar el evento.')
    }
  }

  return (
    <Container sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Calendario
        </Typography>
        {isGlobalAdmin ? (
          <Typography variant="body2" color="text.secondary">
            Selecciona un productId para cargar eventos.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Eventos para tu producto activo.
          </Typography>
        )}
      </Box>

      {isGlobalAdmin ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            label="productId"
            size="small"
            value={selectedProductId}
            onChange={(event) => setSelectedProductId(event.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => fetchEvents(range, selectedProductId)}
          >
            Cargar
          </Button>
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ height: 600, position: 'relative' }}>
        <BigCalendar
          localizer={localizer}
          events={mappedEvents}
          startAccessor="start"
          endAccessor="end"
          selectable
          views={['month', 'week', 'day']}
          onRangeChange={handleRangeChange}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
        />
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            <CircularProgress />
          </Box>
        ) : null}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {form.id ? 'Editar evento' : 'Crear evento'}
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <TextField
            label="Título"
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          <TextField
            label="Descripción"
            value={form.description}
            onChange={(event) =>
              setForm({ ...form, description: event.target.value })
            }
            multiline
            rows={2}
          />
          <TextField
            label="Ubicación"
            value={form.location}
            onChange={(event) =>
              setForm({ ...form, location: event.target.value })
            }
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Inicio"
              type="datetime-local"
              value={form.startAt}
              onChange={(event) =>
                setForm({ ...form, startAt: event.target.value })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fin"
              type="datetime-local"
              value={form.endAt}
              onChange={(event) =>
                setForm({ ...form, endAt: event.target.value })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          {isGlobalAdmin ? (
            <TextField
              label="productId"
              value={form.productId}
              onChange={(event) =>
                setForm({ ...form, productId: event.target.value })
              }
            />
          ) : null}
          <TextField
            label="consumerId (opcional)"
            value={form.consumerId}
            onChange={(event) =>
              setForm({ ...form, consumerId: event.target.value })
            }
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.eventType}
                onChange={(event) =>
                  setForm({ ...form, eventType: event.target.value })
                }
              >
                <MenuItem value="follow_up">Seguimiento</MenuItem>
                <MenuItem value="appointment">Cita</MenuItem>
                <MenuItem value="reminder">Recordatorio</MenuItem>
                <MenuItem value="booking">Reserva</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value })
                }
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="completed">Completado</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          {form.id ? (
            <Button color="error" onClick={handleDelete}>
              Eliminar
            </Button>
          ) : null}
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default CalendarPage
